import {
  createCredentialSchema,
  credentialListSchema,
  credentialSchema,
  rotateCredentialSchema,
  deviceConnectionListSchema,
  deviceConnectionSchema,
  approveDeviceSchema,
  type CreateCredential,
  type RotateCredential,
  connectionListSchema,
  connectionSchema,
  connectionActivityPageSchema,
  type Connection,
  type CreateConnection,
  extensionCatalogueSchema,
  organisationLibrarySchema,
  qualityMetricsSchema,
  scenarioFixtureListSchema,
  skillSampleTaskListSchema,
} from "@zworks/console-contracts";
import {
  projectPoliciesSchema,
  projectSettingsSchema,
  updateProjectPoliciesSchema,
  updateProjectSettingsSchema,
  type UpdateProjectPolicies,
  notificationPreferenceListSchema,
  updateNotificationsSchema,
  projectExportListSchema,
  projectExportSchema,
  clearMemorySchema,
  retentionSchema,
  updateRetentionSchema,
  requestDeletionSchema,
  deletionStateSchema,
  projectDeletionRequestSchema,
  askZotSchema,
  agentUsageSummarySchema,
  confirmZotSchema,
  createZotArtifactProposalSchema,
  createZotArtifactSchema,
  renameZotThreadSchema,
  workItemTraceabilitySchema,
  zotArtifactDetailSchema,
  zotArtifactListSchema,
  zotArtifactSchema,
  zotMessageSchema,
  zotSearchQuerySchema,
  zotSearchResponseSchema,
  zotStreamEventSchema,
  zotThreadListSchema,
  zotThreadMessagesSchema,
  projectFileListSchema,
  projectFileSchema,
  uploadProjectFileSchema,
  modelRouteListSchema,
  createModelRouteSchema,
  type AgentUsageSummary,
  type ZotToolUsage,
  zotToolUsageSchema,
  type AskZot,
  type ConfirmZot,
  type CreateZotArtifact,
  type CreateZotArtifactProposal,
  type ProjectFile,
  type ProjectFileList,
  type RenameZotThread,
  type ZotArtifact,
  type ZotArtifactDetail,
  type ZotArtifactList,
  type ZotMessage,
  type ZotSearchQuery,
  type ZotSearchResponse,
  type ZotStreamEvent,
  type ZotThread,
  type ZotThreadList,
  type ZotThreadMessages,
  type UpdateProjectSettings,
  type NotificationPreferenceList,
  type ProjectExport,
  type Retention,
  type UpdateRetention,
  type ModelRoute,
  type CreateModelRoute,
} from "@zworks/console-contracts";
import {
  flowDraftSchema,
  publishDraftSchema,
  saveFlowDraftSchema,
  validateDraftResponseSchema,
  type DraftStep,
  type FlowDraft,
  type ValidateDraftResponse,
  approvalSchema,
  approvalsInboxSchema,
  auditListResponseSchema,
  auditFilterSchema,
  type AuditCategory,
  type AuditFilter,
  type AuditQuery,
  type AuditEntry,
  artefactListSchema,
  artefactSchema,
  evidencePackSchema,
  evidencePackVerificationSchema,
  billingSettingsSchema,
  updateBillingSettingsSchema,
  changeRoleSchema,
  checkoutSessionSchema,
  createCheckoutSchema,
  createCheckoutSessionSchema,
  createFlowSchema,
  createProjectSchema,
  createWorkItemCommentSchema,
  createWorkItemSchema,
  updateWorkItemSchema,
  workItemCommentListSchema,
  workItemCommentSchema,
  decideApprovalSchema,
  evidenceMatrixSchema,
  flowListSchema,
  flowSchema,
  flowVersionListSchema,
  flowVersionSchema,
  publishFlowVersionSchema,
  invitationListSchema,
  invitationSchema,
  ledgerFilterSchema,
  ledgerResponseSchema,
  meResponseSchema,
  membershipProjectListSchema,
  memoryEntryListSchema,
  memorySourceListSchema,
  createMemorySourceSchema,
  addMemoryFactSchema,
  updateMemoryFactSchema,
  recallMemorySchema,
  serviceMemoryListSchema,
  instructionListSchema,
  instructionSchema,
  saveInstructionSchema,
  restoreInstructionSchema,
  requestToolSchema,
  attachConnectionToolSchema,
  createConnectionSchema,
  installSkillSchema,
  skillInstallListSchema,
  syncRevocationsResultSchema,
  skillInstallSchema,
  toolBindingListSchema,
  toolBindingSchema,
  memoryEntrySchema,
  memorySourceSchema,
  metaSchema,
  onboardingStateSchema,
  overviewSchema,
  paymentSchema,
  problemSchema,
  projectMemberListSchema,
  projectMemberSchema,
  projectSummaryListSchema,
  projectSummarySchema,
  runDetailSchema,
  traceSnapshotSchema,
  runListFilterSchema,
  runListResponseSchema,
  pullRequestListSchema,
  repositoryListSchema,
  gitHubConnectionStateSchema,
  paymentConnectionStateSchema,
  connectRepositoryResultSchema,
  type ConnectRepositoryInput,
  saveOnboardingSchema,
  startRunResponseSchema,
  startRunSchema,
  transferOwnershipSchema,
  walletSchema,
  workItemDetailSchema,
  workItemFilterSchema,
  workItemListResponseSchema,
  workItemSchema,
  modelRouteSchema,
  type Approval,
  type ApprovalsInbox,
  type Artefact,
  type ArtefactList,
  type BillingSettings,
  type UpdateBillingSettings,
  type ChangeRole,
  type CheckoutSession,
  type CreateCheckout,
  type CreateCheckoutSession,
  type CreateFlow,
  type CreateProject,
  type CreateWorkItem,
  type CreateWorkItemComment,
  type DecideApproval,
  type EvidenceMatrix,
  type EvidencePack,
  type EvidencePackVerification,
  type Flow,
  type FlowList,
  type FlowVersion,
  type FlowVersionList,
  type Invitation,
  type LedgerFilter,
  type LedgerResponse,
  type MeResponse,
  type MemoryEntry,
  type MemorySource,
  type CreateMemorySource,
  type AddMemoryFact,
  type UpdateMemoryFact,
  type RecallMemory,
  type ServiceMemory,
  type Instruction,
  type SaveInstruction,
  type AttachConnectionTool,
  type RequestTool,
  type InstallSkill,
  type SkillInstall,
  type ToolBinding,
  type OnboardingState,
  type Overview,
  type Payment,
  type Problem,
  type ProjectMember,
  type ProjectSummary,
  type RunDetail,
  type RunListFilter,
  type RunListResponse,
  type PullRequest,
  type Repository,
  type SaveOnboarding,
  type StartRun,
  type StartRunResponse,
  type TransferOwnership,
  type Wallet,
  type WorkItem,
  type WorkItemComment,
  type WorkItemDetail,
  type WorkItemFilter,
  type WorkItemListResponse,
} from "@zworks/console-contracts";
import {
  agentTaskArtifactListSchema,
  agentTaskDetailSchema,
  agentTaskListSchema,
  agentTaskOptionsSchema,
  agentTaskSchema,
  type AgentTask,
  type StartAgentTask,
} from "@zworks/console-contracts";
import type { ZodType } from "zod";
import { z } from "zod";

/** A non-2xx answer from the API, or a response that does not match its contract. */
export class ApiError extends Error {
  override name = "ApiError";
  readonly status: number;
  readonly problem: Problem | undefined;

  constructor(message: string, status: number, problem?: Problem) {
    super(message);
    this.status = status;
    this.problem = problem;
  }

  /** Quote this to support. It ties the screen to one line in the server log. */
  get requestId(): string | undefined {
    return this.problem?.requestId;
  }
}

async function readProblem(response: Response): Promise<Problem | undefined> {
  try {
    const parsed = problemSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

/**
 * GETs a path on the same origin and validates the body against its contract schema. A body that
 * does not match is an error, never a value the screen has to guess about.
 */
export async function apiGet<T>(
  path: string,
  schema: ZodType<T>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    signal: signal ?? null,
  });
  return readContract(response, schema);
}

/**
 * POSTs to a path on the same origin and validates the body against its contract schema. The
 * invitation token travels only in the path, never in the body, and never reaches the screen.
 */
export async function apiPut<T>(path: string, schema: ZodType<T>, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PUT",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return readContract(response, schema);
}

export async function apiPost<T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? null : JSON.stringify(body),
  });
  return readContract(response, schema);
}

/**
 * PATCHes a path on the same origin and validates the body against its contract schema. Used
 * for the member role change; the body carries the next role only.
 */
export async function apiPatch<T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? null : JSON.stringify(body),
  });
  return readContract(response, schema);
}

/**
 * DELETEs a path on the same origin. A 204 carries no body, so there is nothing to validate;
 * any other 2xx is unexpected and an error.
 */
export async function apiDelete(path: string): Promise<void> {
  const response = await fetch(path, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiError(
      problem?.title ?? `Request failed with status ${response.status}`,
      response.status,
      problem,
    );
  }
}

async function readContract<T>(response: Response, schema: ZodType<T>): Promise<T> {
  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiError(
      problem?.title ?? `Request failed with status ${response.status}`,
      response.status,
      problem,
    );
  }
  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError(
      "The server answered in a shape this app does not understand.",
      response.status,
    );
  }
  return parsed.data;
}

export const metaQuery = {
  queryKey: ["meta"] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet("/api/v1/meta", metaSchema, signal),
};

const zotCommandCatalogueSchema = z.object({
  commands: z.array(
    z.object({
      command: z.string(),
      title: z.string(),
      summary: z.string(),
      doc: z.string().regex(/^docs\/zot\/commands\/[a-z-]+\.md$/),
    }),
  ),
});

export const zotCommandCatalogueQuery = {
  queryKey: ["zot-command-catalogue"] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet("/api/v1/zot/commands", zotCommandCatalogueSchema, signal),
};

export const currentUserQuery = {
  queryKey: ["me"] as const,
  // A failed lookup must not be retried each time a component mounts: the gate unmounts its
  // children while the query is pending, so the retry would loop for ever.
  retryOnMount: false,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet("/api/v1/me", meResponseSchema, signal),
};

export type {
  Approval,
  ApprovalsInbox,
  Artefact,
  ArtefactList,
  AuditCategory,
  AuditEntry,
  AuditFilter,
  AuditQuery,
  CreateCheckout,
  CreateCheckoutSession,
  CheckoutSession,
  CreateFlow,
  CreateWorkItem,
  DecideApproval,
  EvidenceMatrix,
  EvidencePack,
  EvidencePackVerification,
  Flow,
  FlowList,
  FlowVersion,
  FlowVersionList,
  LedgerFilter,
  LedgerResponse,
  MeResponse,
  OnboardingState,
  Overview,
  Payment,
  RunDetail,
  RunListResponse,
  RunListFilter,
  PullRequest,
  Repository,
  StartRun,
  StartRunResponse,
  Wallet,
  WorkItem,
  WorkItemDetail,
  WorkItemFilter,
  WorkItemListResponse,
  MemoryEntry,
  MemorySource,
  CreateMemorySource,
  AddMemoryFact,
  UpdateMemoryFact,
  RecallMemory,
};

export const myProjectsQuery = {
  queryKey: ["me", "projects"] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet("/api/v1/me/projects", membershipProjectListSchema, signal),
};

export function billingQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "billing"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/billing`, billingSettingsSchema, signal),
  };
}

export function updateBilling(
  projectId: string,
  input: UpdateBillingSettings,
): Promise<BillingSettings> {
  return apiPut(
    `${projectPath(projectId)}/billing`,
    billingSettingsSchema,
    updateBillingSettingsSchema.parse(input),
  );
}

export const projectsQuery = {
  queryKey: ["projects"] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiGet("/api/v1/projects", projectSummaryListSchema, signal),
};

/**
 * Creates a project. The caller becomes its owner and the starter grant arrives with it.
 * A taken address answers 409, and a caller who used all 3 grants answers 403.
 */
export function createProject(body: CreateProject): Promise<ProjectSummary> {
  return apiPost("/api/v1/projects", projectSummarySchema, createProjectSchema.parse(body));
}

/**
 * The overview aggregates for one project. Evidence completion and the balance stay null
 * until their tables exist, so the page labels them unknown rather than showing zero.
 */
export function overviewQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "overview"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`/api/v1/projects/${encodeURIComponent(projectId)}/overview`, overviewSchema, signal),
  };
}

/**
 * One page of the project's runs, newest first. The state filter names the Runs board tabs:
 * all, waiting, running, unknown, or failed.
 */
export function runsQuery(projectId: string, filter: RunListFilter, page: number) {
  return {
    queryKey: ["projects", projectId, "runs", filter, page] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({
        state: runListFilterSchema.parse(filter),
        page: String(page),
      });
      return apiGet(
        `/api/v1/projects/${encodeURIComponent(projectId)}/runs?${params.toString()}`,
        runListResponseSchema,
        signal,
      );
    },
  };
}

/**
 * The wallet for one project: the available balance, the monthly limit with its reset date,
 * and the four labelled certainty totals. Unknown stays visible, never zero.
 */
export function walletQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "wallet"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/wallet`, walletSchema, signal),
  };
}

/** Creates a payment for one credit tier. The card number never goes through this API. */
export function createCheckout(projectId: string, body: CreateCheckout): Promise<Payment> {
  return apiPost(
    `${projectPath(projectId)}/checkout`,
    paymentSchema,
    createCheckoutSchema.parse(body),
  );
}

/**
 * Creates a hosted checkout session for one credit tier. Returns the session URL the payer
 * completes; the page opens it and waits for the verified webhook. The card number never
 * goes through this API.
 */
export function createCheckoutSession(
  projectId: string,
  body: CreateCheckoutSession,
): Promise<CheckoutSession> {
  return apiPost(
    `${projectPath(projectId)}/checkout/session`,
    checkoutSessionSchema,
    createCheckoutSessionSchema.parse(body),
  );
}

/**
 * One page of the project's ledger entries, newest first. The kind filter names the Usage
 * board tabs: all, purchases, or usage.
 */
export function ledgerQuery(projectId: string, filter: LedgerFilter, page: number) {
  return {
    queryKey: ["projects", projectId, "ledger", filter, page] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({
        kind: ledgerFilterSchema.parse(filter),
        page: String(page),
      });
      return apiGet(
        `${projectPath(projectId)}/ledger?${params.toString()}`,
        ledgerResponseSchema,
        signal,
      );
    },
  };
}

/**
 * Starts a run. The idempotency key makes a retried submit return the same run. Answers 202
 * on first start and 200 on a repeat; both carry the run id and its pending state.
 */
export function startRun(projectId: string, body: StartRun): Promise<StartRunResponse> {
  return apiPost(
    `${projectPath(projectId)}/runs`,
    startRunResponseSchema,
    startRunSchema.parse(body),
  );
}

/** One run with its step timeline and credit split. Unknown stays visible until reconciled. */
export function runDetailQuery(projectId: string, runId: string) {
  return {
    queryKey: ["projects", projectId, "runs", runId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/runs/${encodeURIComponent(runId)}`,
        runDetailSchema,
        signal,
      ),
  };
}

/** The authenticated project proxy returns a validated trace snapshot. */
export function traceSnapshotQuery(projectId: string, runId: string) {
  return {
    queryKey: ["projects", projectId, "runs", runId, "trace"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/runs/${encodeURIComponent(runId)}/trace`,
        traceSnapshotSchema,
        signal,
      ),
  };
}

/** Cancels a run that can still spend and releases its hold. */
export function cancelRun(projectId: string, runId: string): Promise<StartRunResponse> {
  return apiPost(
    `${projectPath(projectId)}/runs/${encodeURIComponent(runId)}/cancel`,
    startRunResponseSchema,
  );
}

/**
 * One page of the project's work items with their latest runs. The status filter names the
 * WorkItems board tabs, and the search covers the key and the title.
 */
export function workItemsQuery(
  projectId: string,
  filter: WorkItemFilter,
  search: string,
  page: number,
) {
  return {
    queryKey: ["projects", projectId, "work-items", filter, search, page] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({
        status: workItemFilterSchema.parse(filter),
        search,
        page: String(page),
      });
      return apiGet(
        `/api/v1/projects/${encodeURIComponent(projectId)}/work-items?${params.toString()}`,
        workItemListResponseSchema,
        signal,
      );
    },
  };
}

/** The bound repositories for one project. Every response is validated by the shared contract. */
export function memorySourcesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "memory", "sources"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/memory/sources`, memorySourceListSchema, signal),
  };
}

export function createMemorySource(
  projectId: string,
  input: CreateMemorySource,
): Promise<MemorySource> {
  return apiPost(
    `${projectPath(projectId)}/memory/sources`,
    memorySourceSchema,
    createMemorySourceSchema.parse(input),
  );
}

export function reindexMemory(projectId: string): Promise<MemorySource[]> {
  return apiPost(`${projectPath(projectId)}/memory/reindex`, memorySourceListSchema);
}

/** The project's stored manual facts, newest first, for the knowledge graph. */
export function memoryFactsQuery(projectId: string, limit = 20) {
  return {
    queryKey: ["projects", projectId, "memory", "facts", limit] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/memory/facts?limit=${encodeURIComponent(String(limit))}`,
        memoryEntryListSchema,
        signal,
      ),
  };
}

export function recallMemory(projectId: string, input: RecallMemory): Promise<MemoryEntry[]> {
  return apiPost(
    `${projectPath(projectId)}/memory/recall`,
    memoryEntryListSchema,
    recallMemorySchema.parse(input),
  );
}

/** What the shared memory service recalls for the project (503 when it is not configured). */
export function recallServiceMemory(
  projectId: string,
  input: RecallMemory,
): Promise<ServiceMemory[]> {
  return apiPost(
    `${projectPath(projectId)}/memory/service-recall`,
    serviceMemoryListSchema,
    recallMemorySchema.parse(input),
  );
}

export function addMemoryFact(projectId: string, input: AddMemoryFact): Promise<MemoryEntry> {
  return apiPost(
    `${projectPath(projectId)}/memory/facts`,
    memoryEntrySchema,
    addMemoryFactSchema.parse(input),
  );
}

export function updateMemoryFact(
  projectId: string,
  entryId: string,
  input: UpdateMemoryFact,
): Promise<MemoryEntry> {
  return apiPatch(
    `${projectPath(projectId)}/memory/facts/${encodeURIComponent(entryId)}`,
    memoryEntrySchema,
    updateMemoryFactSchema.parse(input),
  );
}

/** The project's current instructions, when any are saved. 404 means none yet. */
export function instructionsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "instructions", "current"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/instructions`, instructionSchema, signal),
  };
}

/** Every saved version, newest first. The list is short: one row per edit or restore. */
export function instructionsHistoryQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "instructions", "history"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/instructions/history`, instructionListSchema, signal),
  };
}

/** Saves a new version. The server redacts secrets before storing and audits the change. */
export function saveInstructions(projectId: string, input: SaveInstruction): Promise<Instruction> {
  return apiPost(
    `${projectPath(projectId)}/instructions`,
    instructionSchema,
    saveInstructionSchema.parse(input),
  );
}

/** Restores a version by saving its body as a new version. History stays append-only. */
export function restoreInstructions(projectId: string, version: number): Promise<Instruction> {
  return apiPost(
    `${projectPath(projectId)}/instructions/restore`,
    instructionSchema,
    restoreInstructionSchema.parse({ version }),
  );
}

/** The tools bound to one project, with capability, credential health, and flows using each. */
export function toolsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "tools"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/tools`, toolBindingListSchema, signal),
  };
}

/** Removes a tool binding. The page already lists the workflows that used it. */
export function removeTool(projectId: string, bindingId: string): Promise<void> {
  return apiDelete(`${projectPath(projectId)}/tools/${encodeURIComponent(bindingId)}`);
}

/** Requests a tool. The server keeps exactly the requested capabilities, never more. */
export function requestTool(projectId: string, input: RequestTool): Promise<ToolBinding> {
  return apiPost(
    `${projectPath(projectId)}/tools`,
    toolBindingSchema,
    requestToolSchema.parse(input),
  );
}

/** Attaches a tool from an existing connection. The server derives risk and access from the scopes. */
export function attachConnectionTool(
  projectId: string,
  connectionId: string,
  input: AttachConnectionTool,
): Promise<ToolBinding> {
  return apiPost(
    `${projectPath(projectId)}/connections/${encodeURIComponent(connectionId)}/tools`,
    toolBindingSchema,
    attachConnectionToolSchema.parse(input),
  );
}

/** The skills installed in one project, with version and update state. */
export function skillsQuery(projectId: string, options: { reconcile?: boolean } = {}) {
  return {
    queryKey: ["projects", projectId, "skills"] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      // Reconcile first, then read: the list is what the page shows, and a withdrawal the
      // platform already enforces must not still read as enabled because nobody pressed a
      // button. A failure here does not fail the read — the enforcement is upstream, so the
      // page showing a stale badge is a display problem, and refusing to show the list because
      // a reconcile call failed would be worse.
      // Whether the reconcile succeeded travels with the list. It used to be swallowed, and
      // the page went on reporting "Up to date" for every skill after the server had said it
      // could not check revocations at all — a claim that the list had been consulted when it
      // had not. The read still succeeds: the list is the best answer available either way.
      let revocationChecked = true;
      if (options.reconcile !== false) {
        try {
          await syncRevocations(projectId);
        } catch {
          revocationChecked = false;
        }
      }
      const list = await apiGet(`${projectPath(projectId)}/skills`, skillInstallListSchema, signal);
      return { list, revocationChecked };
    },
  };
}

/**
 * Reconciles revocation for one project: reads the digests the platform has withdrawn and marks
 * every install of one. Idempotent, so a caller can run it whenever the install list is read.
 */
export function syncRevocations(
  projectId: string,
): Promise<{ revoked: string[]; checked: number }> {
  return apiPost(`${projectPath(projectId)}/skills/revocations/sync`, syncRevocationsResultSchema);
}

/** Workflow scenario fixtures for the Studio evals tab (EV3). Read only. */
export function scenarioFixturesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "eval", "scenario-fixtures"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/eval/scenario-fixtures`, scenarioFixtureListSchema, signal),
  };
}

/** Skill sample tasks for the Studio evals tab (EV4). Read only. */
export function skillSampleTasksQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "eval", "skill-tasks"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/eval/skill-tasks`, skillSampleTaskListSchema, signal),
  };
}

/** Quality and cost metrics for the Evals page (QA20). Read only. */
export function qualityMetricsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "quality", "metrics"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/quality/metrics`, qualityMetricsSchema, signal),
  };
}

/**
 * The published extension catalogue (MK9). Read only. The server returns the first page, and the
 * caller's own runtime decides what each signed-in user may see.
 */
export function extensionCatalogueQuery() {
  return {
    queryKey: ["marketplace", "extensions"] as const,
    initialPageParam: null as string | null,
    queryFn: ({ signal, pageParam }: { signal: AbortSignal; pageParam: string | null }) => {
      const cursor = pageParam === null ? "" : `?cursor=${encodeURIComponent(pageParam)}`;
      return apiGet(`/api/v1/marketplace/extensions${cursor}`, extensionCatalogueSchema, signal);
    },
    getNextPageParam: (lastPage: z.infer<typeof extensionCatalogueSchema>) =>
      lastPage.nextCursor ?? undefined,
  };
}

/**
 * One organisation's private library (MK17). Read only. The server answers not-found to anyone
 * who is not an active member, so the screen cannot tell a missing library from a hidden one.
 */
export function organisationLibraryQuery(organisationId: string) {
  return {
    queryKey: ["organisations", organisationId, "extensions"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `/api/v1/organisations/${encodeURIComponent(organisationId)}/extensions`,
        organisationLibrarySchema,
        signal,
      ),
  };
}

/** Installs a skill. The digest pins the exact content; uploads stay private until approved. */
export function installSkill(projectId: string, input: InstallSkill): Promise<SkillInstall> {
  return apiPost(
    `${projectPath(projectId)}/skills`,
    skillInstallSchema,
    installSkillSchema.parse(input),
  );
}

/** Removes a skill install. The page already lists the workflows that used it. */
export function removeSkill(projectId: string, installId: string): Promise<void> {
  return apiDelete(`${projectPath(projectId)}/skills/${encodeURIComponent(installId)}`);
}

/** Approves a private install, turning it enabled so a workflow may bind it. */
export function approveSkill(
  projectId: string,
  installId: string,
  reason?: string,
): Promise<SkillInstall> {
  return apiPost(
    `${projectPath(projectId)}/skills/${encodeURIComponent(installId)}/approve`,
    skillInstallSchema,
    reason === undefined ? {} : { reason },
  );
}

export function repositoriesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "repositories"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/repositories`, repositoryListSchema, signal),
  };
}

/** The GitHub App connection state for one project, including the declared permissions. */
export function gitHubConnectionQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "integrations", "github"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/integrations/github`, gitHubConnectionStateSchema, signal),
  };
}

/** The payment connection state for one project: status, test mode, and probe health. */
export function paymentConnectionQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "integrations", "payments"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/integrations/payments`,
        paymentConnectionStateSchema,
        signal,
      ),
  };
}

/** Starts the GitHub App installation; the server returns the install URL the browser opens. */
export function startGitHubInstall(projectId: string): Promise<{ installUrl: string | null }> {
  return apiPost(
    `${projectPath(projectId)}/integrations/github/install`,
    z.object({ installUrl: z.string().nullable() }),
  );
}

/** Connects a repository through the hub, which verifies the installation reads it. */
export function connectHubRepository(projectId: string, body: ConnectRepositoryInput) {
  return apiPost(`${projectPath(projectId)}/hub/repositories`, connectRepositoryResultSchema, body);
}

/** What an agent task can target: writable repositories, the branch rule, the profile, and the live customer-key references. */
export function agentTaskOptionsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "agent-tasks", "options"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/agent-tasks/options`, agentTaskOptionsSchema, signal),
  };
}

export function agentTasksQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "agent-tasks"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/agent-tasks`, agentTaskListSchema, signal),
  };
}

export function agentTaskDetailQuery(projectId: string, taskId: string) {
  return {
    queryKey: ["projects", projectId, "agent-tasks", taskId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/agent-tasks/${taskId}`, agentTaskDetailSchema, signal),
  };
}

/** The stored results of a task (503 when the console has task data switched off). */
export function agentTaskArtifactsQuery(projectId: string, taskId: string) {
  return {
    queryKey: ["projects", projectId, "agent-tasks", taskId, "artifacts"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/agent-tasks/${taskId}/artifacts`,
        agentTaskArtifactListSchema,
        signal,
      ),
  };
}

/** Where the browser downloads one stored file of a task. */
export function agentTaskArtifactFileUrl(
  projectId: string,
  taskId: string,
  revisionId: string,
  path: string,
): string {
  return `${projectPath(projectId)}/agent-tasks/${taskId}/artifacts/${encodeURIComponent(revisionId)}/file?path=${encodeURIComponent(path)}`;
}

export function startAgentTask(projectId: string, body: StartAgentTask): Promise<AgentTask> {
  return apiPost(`${projectPath(projectId)}/agent-tasks`, agentTaskSchema, body);
}

export function sendAgentTaskFeedback(projectId: string, taskId: string, text: string) {
  return apiPost(`${projectPath(projectId)}/agent-tasks/${taskId}/feedback`, z.unknown(), { text });
}

export function cancelAgentTask(projectId: string, taskId: string) {
  return apiPost(`${projectPath(projectId)}/agent-tasks/${taskId}/cancel`, z.unknown());
}

/** Pull requests opened by runs in one project, including checks and review state. */
export function pullRequestsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "pull-requests"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/pull-requests`, pullRequestListSchema, signal),
  };
}

/** One work item with its runs newest first, for the drawer. */
export function workItemDetailQuery(projectId: string, key: string) {
  return {
    queryKey: ["projects", projectId, "work-items", key] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/work-items/${encodeURIComponent(key)}`,
        workItemDetailSchema,
        signal,
      ),
  };
}

/**
 * Creates a work item. Keys read ABC-123 and are unique per project: a taken key answers 409,
 * and only owners and developers may create.
 */
export function createWorkItem(projectId: string, body: CreateWorkItem): Promise<WorkItem> {
  return apiPost(
    `${projectPath(projectId)}/work-items`,
    workItemSchema,
    createWorkItemSchema.parse(body),
  );
}

export function updateWorkItemStatus(
  projectId: string,
  key: string,
  status: WorkItem["status"],
): Promise<WorkItem> {
  return apiPatch(
    `${projectPath(projectId)}/work-items/${encodeURIComponent(key)}`,
    workItemSchema,
    updateWorkItemSchema.parse({ status }),
  );
}

export function workItemCommentsQuery(projectId: string, key: string) {
  return {
    queryKey: ["projects", projectId, "work-items", key, "comments"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/work-items/${encodeURIComponent(key)}/comments`,
        workItemCommentListSchema,
        signal,
      ),
  };
}

export function createWorkItemComment(
  projectId: string,
  key: string,
  body: CreateWorkItemComment,
): Promise<WorkItemComment> {
  return apiPost(
    `${projectPath(projectId)}/work-items/${encodeURIComponent(key)}/comments`,
    workItemCommentSchema,
    createWorkItemCommentSchema.parse(body),
  );
}

/**
 * The flows of one project, each with state, version, step and gate counts, readiness, and
 * the last run. An empty list is a state, not an error: the page offers the new-flow action.
 */
export function flowsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "flows"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/flows`, flowListSchema, signal),
  };
}

/** Creates a draft flow. Only owners and developers may create. */
export function createFlow(projectId: string, body: CreateFlow): Promise<Flow> {
  return apiPost(`${projectPath(projectId)}/flows`, flowSchema, createFlowSchema.parse(body));
}

/** The versions of one flow, oldest first, so the history can diff any two. */
export function flowVersionsQuery(projectId: string, flowId: string) {
  return {
    queryKey: ["projects", projectId, "flows", flowId, "versions"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/versions`,
        flowVersionListSchema,
        signal,
      ),
  };
}

/**
 * Publishes a new version of a flow. Publishing inserts a new row and never changes an
 * existing one, so a version a run is pinned to stays exactly as it was.
 */
export function publishFlowVersion(
  projectId: string,
  flowId: string,
  body: { steps: FlowVersion["steps"] },
): Promise<FlowVersion> {
  return apiPost(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/versions`,
    flowVersionSchema,
    publishFlowVersionSchema.parse(body),
  );
}

/**
 * Activates a flow when its current version is ready. A flow that deploys without a gate
 * answers 422 with the reason, so the page can say what is missing.
 */
export function activateFlow(projectId: string, flowId: string): Promise<Flow> {
  return apiPost(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/activate`,
    flowSchema,
  );
}

/**
 * One page of the project's audit log, newest first. The category filter names the Audit board
 * tabs: all, security, approvals, members, credits, or runs.
 */
export function auditListQuery(projectId: string, category: AuditFilter, page: number) {
  return {
    queryKey: ["projects", projectId, "audit", category, page] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({
        category: auditFilterSchema.parse(category),
        page: String(page),
      });
      return apiGet(
        `${projectPath(projectId)}/audit?${params.toString()}`,
        auditListResponseSchema,
        signal,
      );
    },
  };
}

/**
 * The path of the CSV export for one filter. It is a plain link, so the browser downloads it
 * with the same filter the page is showing and the range matches exactly.
 */
export function auditExportPath(projectId: string, category: AuditFilter): string {
  const params = new URLSearchParams({ category: auditFilterSchema.parse(category) });
  return `${projectPath(projectId)}/audit/export?${params.toString()}`;
}

/**
 * The approvals inbox, split into waiting for the caller, waiting for others, and decided.
 * Eligibility arrives from the server, so the page never guesses who may decide.
 */
export function approvalsInboxQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "approvals"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/approvals`, approvalsInboxSchema, signal),
  };
}

/** One approval, found in the inbox by id. Null when the inbox has not loaded or lacks it. */
export function approvalDetailQuery(projectId: string, approvalId: string) {
  return {
    queryKey: ["projects", projectId, "approvals", approvalId] as const,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<Approval | null> => {
      const inbox = await apiGet(
        `${projectPath(projectId)}/approvals`,
        approvalsInboxSchema,
        signal,
      );
      const all = [...inbox.waitingForMe, ...inbox.waitingForOthers, ...inbox.decided];
      return all.find((entry) => entry.id === approvalId) ?? null;
    },
  };
}

/**
 * Decides a pending approval. Carries the digest and revision the caller reviewed: a stale pair
 * answers 409 with what moved and records nothing.
 */
export function decideApproval(
  projectId: string,
  approvalId: string,
  body: DecideApproval,
): Promise<Approval> {
  return apiPost(
    `${projectPath(projectId)}/approvals/${encodeURIComponent(approvalId)}/decide`,
    approvalSchema,
    decideApprovalSchema.parse(body),
  );
}

/**
 * The six-cell evidence matrix for one run. Missing is returned as missing, never omitted,
 * so the page shows the gap honestly.
 */
export function evidenceMatrixQuery(projectId: string, runId: string) {
  return {
    queryKey: ["projects", projectId, "runs", runId, "evidence"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/runs/${encodeURIComponent(runId)}/evidence`,
        evidenceMatrixSchema,
        signal,
      ),
  };
}

/**
 * Work item traceability for a project - maps work items to their test cases and results.
 */
export function traceabilityQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "quality", "traceability"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/quality/traceability`,
        z.object({ items: z.array(workItemTraceabilitySchema) }),
        signal,
      ),
  };
}

/** One artefact: its type, producing step, size, digest, verifier, and retention date. */
export function artefactDetailQuery(projectId: string, artefactId: string) {
  return {
    queryKey: ["projects", projectId, "artefacts", artefactId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/artefacts/${encodeURIComponent(artefactId)}`,
        artefactSchema,
        signal,
      ),
  };
}

/**
 * Builds the evidence pack manifest for one run: artefact rows with ids and digests, never
 * content. The download links are created when the caller presses the button.
 */
export function exportEvidencePack(
  projectId: string,
  body: { runId: string },
): Promise<ArtefactList> {
  return apiPost(`${projectPath(projectId)}/evidence/export`, artefactListSchema, body);
}

/**
 * Builds the signed evidence pack for one run: artefact digests, test results, and
 * approval records, with an HMAC-SHA256 signature. The pack downloads as a file.
 */
export function buildEvidencePack(
  projectId: string,
  body: { runId: string },
): Promise<EvidencePack> {
  return apiPost(`${projectPath(projectId)}/evidence/pack`, evidencePackSchema, body);
}

/** Verifies a pack's signature against the server-held key. */
export function verifyEvidencePack(
  projectId: string,
  pack: EvidencePack,
): Promise<EvidencePackVerification> {
  return apiPost(
    `${projectPath(projectId)}/evidence/pack/verify`,
    evidencePackVerificationSchema,
    pack,
  );
}

export function invitationQuery(token: string) {
  return {
    queryKey: ["invitation", token] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`/api/v1/invitations/${encodeURIComponent(token)}`, invitationSchema, signal),
  };
}

/**
 * Saves the onboarding step payload and the current step, so a reload resumes at the same step.
 * Records completion or skip when `completed` is set; once set, the flow never shows again.
 */
export function saveOnboarding(body: SaveOnboarding): Promise<OnboardingState> {
  return apiPost("/api/v1/me/onboarding", onboardingStateSchema, saveOnboardingSchema.parse(body));
}

/**
 * Accepts an invitation. Accepting twice lands on the same membership: the second accept answers
 * with the already-accepted problem rather than a second row.
 */
export function acceptInvitation(token: string): Promise<Invitation> {
  return apiPost(`/api/v1/invitations/${encodeURIComponent(token)}/accept`, invitationSchema);
}

export interface CreatedInvitationResult {
  invitation: Invitation;
  token: string;
  delivered?: boolean;
}

const createdInvitationPayloadSchema = z.object({
  invitation: invitationSchema,
  token: z.string(),
  delivered: z.boolean().optional(),
});

/**
 * Invites one person to a project. Returns the created invitation, the raw token, and whether
 * email delivery succeeded.
 */
export async function inviteMember(
  projectId: string,
  body: { email: string; role: Invitation["role"] },
): Promise<CreatedInvitationResult> {
  return apiPost(
    `/api/v1/projects/${encodeURIComponent(projectId)}/invitations`,
    createdInvitationPayloadSchema,
    body,
  );
}

const projectPath = (projectId: string) => `/api/v1/projects/${encodeURIComponent(projectId)}`;

export function projectMembersQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "members"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/members`, projectMemberListSchema, signal),
  };
}

export function projectInvitationsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "invitations"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/invitations`, invitationListSchema, signal),
  };
}

/**
 * Changes a member's role. Only an owner may call it; demoting the last owner answers 409 and
 * changes nothing.
 */
export function changeMemberRole(
  projectId: string,
  userId: string,
  body: ChangeRole,
): Promise<ProjectMember> {
  return apiPatch(
    `${projectPath(projectId)}/members/${encodeURIComponent(userId)}`,
    projectMemberSchema,
    changeRoleSchema.parse(body),
  );
}

/** Removes a member from a project. Only an owner may call it. */
export function removeMember(projectId: string, userId: string): Promise<void> {
  return apiDelete(`${projectPath(projectId)}/members/${encodeURIComponent(userId)}`);
}

/**
 * Transfers ownership to another member. The caller becomes a viewer in the same step, so the
 * project keeps exactly one owner.
 */
export function transferOwnership(
  projectId: string,
  body: TransferOwnership,
): Promise<ProjectMember> {
  return apiPost(
    `${projectPath(projectId)}/transfer`,
    projectMemberSchema,
    transferOwnershipSchema.parse(body),
  );
}

/**
 * Resends an invitation with a fresh token. Returns the updated invitation, the fresh raw token,
 * and whether email delivery succeeded.
 */
export function resendInvitation(
  projectId: string,
  invitationId: string,
): Promise<CreatedInvitationResult> {
  return apiPost(
    `${projectPath(projectId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    createdInvitationPayloadSchema,
  );
}

/** Revokes an invitation. Only an owner may call it. */
export function revokeInvitation(projectId: string, invitationId: string): Promise<Invitation> {
  return apiPost(
    `${projectPath(projectId)}/invitations/${encodeURIComponent(invitationId)}/revoke`,
    invitationSchema,
  );
}

/** The saved draft of a flow, or a 404 problem when no draft has been saved yet. */
export function flowDraftQuery(projectId: string, flowId: string) {
  return {
    queryKey: ["projects", projectId, "flows", flowId, "draft"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/draft`,
        flowDraftSchema,
        signal,
      ),
  };
}

/** Saves the draft. The server orders the steps by position. */
export function saveFlowDraft(
  projectId: string,
  flowId: string,
  steps: DraftStep[],
  workflowCap?: number | null,
): Promise<FlowDraft> {
  return apiPut(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/draft`,
    flowDraftSchema,
    saveFlowDraftSchema.parse({ steps, workflowCap: workflowCap ?? null }),
  );
}

/** Checks the steps against the flow rules. Each issue names the step it belongs to. */
export function validateFlowDraft(
  projectId: string,
  flowId: string,
  steps: DraftStep[],
  workflowCap?: number | null,
): Promise<ValidateDraftResponse> {
  return apiPost(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/validate`,
    validateDraftResponseSchema,
    saveFlowDraftSchema.parse({ steps, workflowCap: workflowCap ?? null }),
  );
}

/**
 * Activates the draft as a new version. Runs already pinned keep their version; only runs that
 * start afterwards use the new one.
 */
export function activateFlowDraft(
  projectId: string,
  flowId: string,
  steps: DraftStep[],
  workflowCap?: number | null,
  approvalId?: string | null,
): Promise<FlowVersion> {
  return apiPost(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/draft/activate`,
    flowVersionSchema,
    publishDraftSchema.parse({
      steps,
      workflowCap: workflowCap ?? null,
      approvalId: approvalId ?? null,
    }),
  );
}

/**
 * Asks an owner or approver to confirm publishing a workflow that deploys. The server refuses a
 * draft that breaks a rule, so an unverified version never reaches an approver.
 */
export function requestFlowPublish(
  projectId: string,
  flowId: string,
  steps: DraftStep[],
  workflowCap?: number | null,
): Promise<Approval> {
  return apiPost(
    `${projectPath(projectId)}/flows/${encodeURIComponent(flowId)}/draft/publish-request`,
    approvalSchema,
    saveFlowDraftSchema.parse({ steps, workflowCap: workflowCap ?? null }),
  );
}

export function projectSettingsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "settings"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/settings`, projectSettingsSchema, signal),
  };
}
export function updateProjectSettings(projectId: string, input: UpdateProjectSettings) {
  return apiPut(
    `${projectPath(projectId)}/settings`,
    projectSettingsSchema,
    updateProjectSettingsSchema.parse(input),
  );
}
export function archiveProject(projectId: string) {
  return apiPost(`${projectPath(projectId)}/archive`, projectSettingsSchema);
}
export function projectPoliciesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "policies"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/policies`, projectPoliciesSchema, signal),
  };
}
export function updateProjectPolicies(projectId: string, input: UpdateProjectPolicies) {
  return apiPut(
    `${projectPath(projectId)}/policies`,
    projectPoliciesSchema,
    updateProjectPoliciesSchema.parse(input),
  );
}
export function retentionQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "retention"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(projectPath(projectId) + "/retention", retentionSchema, signal),
  };
}
export function updateRetention(projectId: string, input: UpdateRetention): Promise<Retention> {
  return apiPut(
    projectPath(projectId) + "/retention",
    retentionSchema,
    updateRetentionSchema.parse(input),
  );
}
export function projectExportsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "exports"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(projectPath(projectId) + "/exports", projectExportListSchema, signal),
  };
}
export function requestProjectExport(projectId: string): Promise<ProjectExport> {
  return apiPost(projectPath(projectId) + "/export", projectExportSchema);
}
export function projectExportDownloadPath(projectId: string, exportId: string): string {
  return projectPath(projectId) + "/exports/" + encodeURIComponent(exportId) + "/download";
}
export function clearProjectMemory(projectId: string): Promise<{ removedEntries: number }> {
  return apiPost(
    projectPath(projectId) + "/memory/clear",
    z.object({ removedEntries: z.number().int().nonnegative() }),
    clearMemorySchema.parse({}),
  );
}
export function requestProjectDeletion(projectId: string) {
  return apiPost(
    projectPath(projectId) + "/delete",
    z.object({ state: deletionStateSchema }),
    requestDeletionSchema.parse({ confirm: true }),
  );
}
export function projectDeletionQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "deletion"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(projectPath(projectId) + "/delete", projectDeletionRequestSchema, signal),
  };
}
export function notificationsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "notifications"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/notifications`, notificationPreferenceListSchema, signal),
  };
}
export function updateNotifications(projectId: string, preferences: NotificationPreferenceList) {
  return apiPut(
    `${projectPath(projectId)}/notifications`,
    notificationPreferenceListSchema,
    updateNotificationsSchema.parse({ preferences }),
  );
}

export function settingsAccessLost(error: unknown) {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

/** Only metadata enters the query cache. */
export function credentialsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "credentials"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/credentials`, credentialListSchema, signal),
  };
}
export function devicesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "devices"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/devices`, deviceConnectionListSchema, signal),
  };
}
export function createCredential(projectId: string, input: CreateCredential) {
  return apiPost(
    `${projectPath(projectId)}/credentials`,
    credentialSchema,
    createCredentialSchema.parse(input),
  );
}
export function rotateCredential(projectId: string, ref: string, input: RotateCredential) {
  return apiPost(
    `${projectPath(projectId)}/credentials/${encodeURIComponent(ref)}/rotate`,
    credentialSchema,
    rotateCredentialSchema.parse(input),
  );
}
export function approveDevice(projectId: string, code: string) {
  return apiPost(
    `${projectPath(projectId)}/devices/approve`,
    deviceConnectionSchema,
    approveDeviceSchema.parse({ projectId, code }),
  );
}
export function revokeDevice(projectId: string, id: string) {
  return apiDelete(`${projectPath(projectId)}/devices/${encodeURIComponent(id)}`);
}

export function modelRoutesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "model-routes"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/model-routes`, modelRouteListSchema, signal),
  };
}

export function createModelRoute(projectId: string, input: CreateModelRoute): Promise<ModelRoute> {
  return apiPost(
    `${projectPath(projectId)}/model-routes`,
    modelRouteSchema,
    createModelRouteSchema.parse(input),
  );
}

export function askZot(projectId: string, input: AskZot): Promise<ZotMessage> {
  return apiPost(
    `${projectPath(projectId)}/zot/messages`,
    zotMessageSchema,
    askZotSchema.parse(input),
  );
}

export function confirmZot(projectId: string, input: ConfirmZot): Promise<ZotMessage> {
  return apiPost(
    `${projectPath(projectId)}/zot/confirm`,
    zotMessageSchema,
    confirmZotSchema.parse(input),
  );
}

export function searchZot(projectId: string, input: ZotSearchQuery): Promise<ZotSearchResponse> {
  return apiPost(
    `${projectPath(projectId)}/zot/search`,
    zotSearchResponseSchema,
    zotSearchQuerySchema.parse(input),
  );
}

export function zotMessagesQuery(projectId: string, threadId: string) {
  return {
    queryKey: ["projects", projectId, "zot", threadId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId)}/messages`,
        zotThreadMessagesSchema,
        signal,
      ) satisfies Promise<ZotThreadMessages>,
  };
}

export function zotThreadsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "zot", "threads"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/threads`,
        zotThreadListSchema,
        signal,
      ) satisfies Promise<ZotThreadList>,
  };
}

export function renameZotThread(
  projectId: string,
  threadId: string,
  input: RenameZotThread,
): Promise<ZotThread> {
  return apiPatch(
    `${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId)}`,
    zotThreadListSchema.element,
    renameZotThreadSchema.parse(input),
  );
}

export function deleteZotThread(projectId: string, threadId: string): Promise<void> {
  return apiDelete(`${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId)}`);
}

/** The latest version of each artifact in one chat thread, oldest first. */
export function zotArtifactsQuery(projectId: string, threadId: string | null) {
  return {
    queryKey: ["projects", projectId, "zot", threadId, "artifacts"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId ?? "")}/artifacts`,
        zotArtifactListSchema,
        signal,
      ) satisfies Promise<ZotArtifactList>,
  };
}

/** One artifact version. */
export function zotArtifactQuery(projectId: string, artifactId: string | null) {
  return {
    queryKey: ["projects", projectId, "zot", "artifacts", artifactId] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/artifacts/${encodeURIComponent(artifactId ?? "")}`,
        zotArtifactDetailSchema,
        signal,
      ) satisfies Promise<ZotArtifactDetail>,
  };
}

/** Every stored version of one artefact key in a thread, oldest first. */
export function zotArtifactVersionsQuery(
  projectId: string,
  threadId: string | null,
  artifactKey: string | null,
) {
  return {
    queryKey: [
      "projects",
      projectId,
      "zot",
      threadId,
      "artifacts",
      artifactKey,
      "versions",
    ] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId ?? "")}/artifacts/${encodeURIComponent(artifactKey ?? "")}/versions`,
        zotArtifactListSchema,
        signal,
      ) satisfies Promise<ZotArtifactList>,
  };
}

/** Stores a new artifact version in the thread. */
export function createZotArtifact(
  projectId: string,
  threadId: string,
  input: CreateZotArtifact,
): Promise<ZotArtifact> {
  return apiPost(
    `${projectPath(projectId)}/zot/threads/${encodeURIComponent(threadId)}/artifacts`,
    zotArtifactSchema,
    createZotArtifactSchema.parse(input),
  );
}

/** Appends a footer action as a proposal message in the artifact's thread. */
export function createZotArtifactProposal(
  projectId: string,
  artifactId: string,
  input: CreateZotArtifactProposal,
): Promise<ZotMessage> {
  return apiPost(
    `${projectPath(projectId)}/zot/artifacts/${encodeURIComponent(artifactId)}/proposals`,
    zotMessageSchema,
    createZotArtifactProposalSchema.parse(input),
  );
}

/**
 * Parses one SSE stream of Zot events. Each event carries a JSON body; unknown event types are
 * ignored so the reader stays forward compatible. A non-2xx answer raises an ApiError with the
 * decoded problem, matching the non-streaming askZot behaviour.
 */
export async function* readZotStream(
  projectId: string,
  input: AskZot,
  signal?: AbortSignal,
): AsyncGenerator<ZotStreamEvent> {
  const response = await fetch(`${projectPath(projectId)}/zot/stream`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
    body: JSON.stringify(askZotSchema.parse(input)),
    signal: signal ?? null,
  });
  if (!response.ok || !response.body) {
    let problem: Problem | undefined;
    try {
      problem = problemSchema.safeParse(await response.clone().json()).data ?? undefined;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      problem?.title ?? `Request failed with status ${response.status}`,
      response.status,
      problem,
    );
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventType = "";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let boundary = buffer.indexOf("\n\n");
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        for (const line of block.split("\n")) {
          if (line.startsWith("event:")) eventType = line.slice(6).trim();
          else if (line.startsWith("data:")) {
            try {
              // The event kind travels in the `event:` line. `done` nests the saved message
              // under `message`; every other event carries a flat payload.
              const payload = JSON.parse(line.slice(5).trim()) as Record<string, unknown>;
              const body =
                eventType === "done"
                  ? { type: eventType, message: payload }
                  : { ...payload, type: eventType || payload.type };
              const parsed = zotStreamEventSchema.safeParse(body);
              if (parsed.success && (eventType === "" || parsed.data.type === eventType))
                yield parsed.data;
            } catch {
              // Skip a malformed event; the trailing done event still closes the answer.
            }
          }
        }
        eventType = "";
        boundary = buffer.indexOf("\n\n");
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** Fair-use token totals for the project and the caller. Chats stay free under the cap. */
export function zotUsageQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "zot", "usage"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/usage`,
        agentUsageSummarySchema,
        signal,
      ) satisfies Promise<AgentUsageSummary>,
  };
}

/** Tool calls, proposal outcomes, and settled credits per tool and per chat. Every member may read. */
export function zotToolUsageQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "zot", "tool-usage"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/zot/tool-usage`,
        zotToolUsageSchema,
        signal,
      ) satisfies Promise<ZotToolUsage>,
  };
}

/** The stored project files, newest first, with their index state. Every role may read. */
export function projectFilesQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "files"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(
        `${projectPath(projectId)}/files`,
        projectFileListSchema,
        signal,
      ) satisfies Promise<ProjectFileList>,
  };
}

/** Uploads one file's bytes for server-side extraction. Only owners and developers may write. */
export function uploadProjectFile(
  projectId: string,
  input: {
    name: string;
    kind: string;
    mimeType: string;
    sizeBytes: number;
    contentBase64: string;
  },
): Promise<ProjectFile> {
  return apiPost(
    `${projectPath(projectId)}/files`,
    projectFileSchema,
    uploadProjectFileSchema.parse(input),
  );
}

/** Removes a project file. Only owners and developers may remove. */
export function removeProjectFile(projectId: string, fileId: string): Promise<void> {
  return apiDelete(`${projectPath(projectId)}/files/${encodeURIComponent(fileId)}`);
}

/**
 * Creates a connection. The hub decides the status and the credential reference; the caller
 * names the provider, the auth method, and the scopes it wants.
 */
export function createConnection(projectId: string, input: CreateConnection): Promise<Connection> {
  const parsed = createConnectionSchema.parse(input);
  return apiPost(`${projectPath(projectId)}/connections`, connectionSchema, parsed);
}

/** Lists all hub connections for a project with health status. */
export function connectionsQuery(projectId: string) {
  return {
    queryKey: ["projects", projectId, "connections"] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet(`${projectPath(projectId)}/connections`, connectionListSchema, signal),
  };
}

/**
 * Lists the redacted Hub activity for one connection in the current project, one page at a time,
 * oldest first. Each page's nextCursor reads the next, older page; a null cursor is the last page.
 */
export function connectionActivityQuery(projectId: string, connectionId: string) {
  return {
    queryKey: ["projects", projectId, "connections", connectionId, "activity"] as const,
    initialPageParam: null as string | null,
    queryFn: ({ signal, pageParam }: { signal: AbortSignal; pageParam: string | null }) => {
      const cursor = pageParam === null ? "" : `?cursor=${encodeURIComponent(pageParam)}`;
      return apiGet(
        `${projectPath(projectId)}/connections/${encodeURIComponent(connectionId)}/activity${cursor}`,
        connectionActivityPageSchema,
        signal,
      );
    },
    getNextPageParam: (lastPage: z.infer<typeof connectionActivityPageSchema>) =>
      lastPage.nextCursor ?? undefined,
  };
}
